/** biome-ignore-all lint/style/useNamingConvention: camel case */
import cx from 'classnames';
import {useMemo} from 'react';
import {shallowEqual, useSelector} from 'react-redux';
import {useIsLargishAndUp} from '~/common/hooks/useMatchQuery';
import {isDraft} from '~/common/utils/event_utils';
import {Label} from '~/ViewInvitation/common/Label';
import {isPreview} from '~/ViewInvitation/utils';
import {Divider} from '~library/atoms/Divider';
import {Typography} from '~library/atoms/Typography';
import {FormattedDateString} from './FormattedDateString';
import {HostDetails} from './HostDetails';
import {HostMessage} from './HostMessage';
import {LocationBlock} from './LocationBlock';

export const DetailsModule = ({isCreateEditPreview = false}) => {
  const {
    hostName,
    locationName,
    title,
    streetAddress,
    meetingUrl,
    phoneNumber,
    end: endDate,
    start: startDate,
    isPremium,
    guestsOfHonor,
    eventType,
    cohost,
    host_ids: hostIds,
    status,
  } = useSelector(
    ({eventDetails: ed, details: d, eviteEvent: ev}) => ({
      ...ed,
      host_ids: ed?.host_ids ?? ev?.host_ids ?? [],
      pending_host_ids: ed?.pending_host_ids ?? ev?.pending_host_ids ?? [],
      cohost: {
        name: ed.cohost?.name ?? ev?.cohost_name1 ?? '',
        email: ed.cohost?.email ?? ev?.cohost_email1 ?? '',
      },
      end: d?.end ?? ed.end,
      start: d?.start ?? ed.start,
    }),
    shallowEqual
  );
  const isDesktop = useIsLargishAndUp();
  const messageFromHost = useSelector((state) => state.eventDetails?.messageFromHost ?? null);
  const timezoneAbbr = useSelector((state) => state.coverData?.known_timezone_abbr ?? '');
  const guestPreviews = useSelector(
    (state) => state?.whoIsComing?.guestPreviews ?? [],
    shallowEqual
  );

  const guestOfHonorList = useMemo(() => {
    if (isDraft(status)) return guestsOfHonor.guests;
    return guestPreviews
      .filter((gp) => gp.guestOfHonor)
      .map((gp) => ({name: gp.guestName, userId: gp.userId}));
  }, [guestsOfHonor, status, guestPreviews]);

  const preview = isCreateEditPreview || isPreview();
  const shouldShowLocationBlock = Boolean(locationName || streetAddress || meetingUrl);

  return (
    <div
      className={cx('view-invitation__event-info', {
        'view-invitation__event-info__as-grid': isDesktop && shouldShowLocationBlock,
      })}
      id="event-details"
    >
      <Typography
        variant="header2"
        data-qa-id="event-title"
        classNames="view-invitation__event-title"
        color={title || !preview ? 'charcoal' : 'pebble'}
      >
        {title || (preview ? 'Your event title' : '')}
      </Typography>

      <div
        className={cx('view-invitation__event-info__column', {
          'view-invitation__event-info__column__as-grid': isDesktop && !shouldShowLocationBlock,
        })}
      >
        <div>
          <Label value="when" data-qa-id="when" />
          <FormattedDateString
            start={startDate}
            end={endDate}
            timezoneAbbr={timezoneAbbr}
            preview={preview}
          />
        </div>

        {!isDesktop && shouldShowLocationBlock && <LocationBlock />}

        <HostDetails
          hostName={hostName}
          hostIds={hostIds}
          guestsOfHonor={guestOfHonorList}
          cohost={cohost}
          phoneNumber={phoneNumber}
          status={status}
        />
      </div>
      {isDesktop && shouldShowLocationBlock && (
        <div className="view-invitation__event-info__column">
          <LocationBlock />
        </div>
      )}
      {messageFromHost && (
        <div className="view-invitation__host-message-wrapper">
          <Divider className="view-invitation__details-divider" />
          <div>
            <Label value="Host Note" data-qa-id="event-description" />
            <HostMessage
              messageFromHost={messageFromHost}
              isPremium={isPremium}
              eventType={eventType}
            />
          </div>
        </div>
      )}
    </div>
  );
};
